import { MeasureValue } from "~/types";
import { ASSET_CLASS_IDS } from "~/consts";
import FnlSummariesManager from "~/schemas/dbt/FnlSummariesManager";
import { calculatePercentChange } from "~/utils/calculatePercentChange";
import { getMeasureValue } from "~/utils/getMeasureValue";

// // // //

/**
 * Calculates RWA holders count by subtracting stablecoin holders from total holders
 */
export function getRwaHoldersFromManager(
    manager: Manager,
): MeasureValue | null {
    const totalHolders = getMeasureValue(manager, "holding_addresses_count");

    if (totalHolders === null) {
        return null;
    }

    const stablecoinStats = manager.asset_class_stats?.find(
        (ac) => ac.id === ASSET_CLASS_IDS.Stablecoin,
    );

    if (!stablecoinStats || !stablecoinStats.holding_addresses_count?.val) {
        return totalHolders;
    }

    const stablecoinHolders = stablecoinStats.holding_addresses_count;

    // Calculate RWA holders by subtracting stablecoin holders
    const val = totalHolders.val - stablecoinHolders.val;
    const val_7d =
        totalHolders.val_7d !== null && stablecoinHolders.val_7d !== null
            ? totalHolders.val_7d - stablecoinHolders.val_7d
            : null;

    const val_30d =
        totalHolders.val_30d !== null && stablecoinHolders.val_30d !== null
            ? totalHolders.val_30d - stablecoinHolders.val_30d
            : null;

    const val_90d =
        totalHolders.val_90d !== null && stablecoinHolders.val_90d !== null
            ? totalHolders.val_90d - stablecoinHolders.val_90d
            : null;

    const chg_7d_amt =
        totalHolders.chg_7d_amt !== null &&
        stablecoinHolders.chg_7d_amt !== null
            ? totalHolders.chg_7d_amt - stablecoinHolders.chg_7d_amt
            : null;

    const chg_30d_amt =
        totalHolders.chg_30d_amt !== null &&
        stablecoinHolders.chg_30d_amt !== null
            ? totalHolders.chg_30d_amt - stablecoinHolders.chg_30d_amt
            : null;

    const chg_90d_amt =
        totalHolders.chg_90d_amt !== null &&
        stablecoinHolders.chg_90d_amt !== null
            ? totalHolders.chg_90d_amt - stablecoinHolders.chg_90d_amt
            : null;

    // Calculate percentage changes
    const chg_7d_pct = calculatePercentChange({
        initialValue: val_7d,
        finalValue: val,
    });
    const chg_30d_pct = calculatePercentChange({
        initialValue: val_30d,
        finalValue: val,
    });
    const chg_90d_pct = calculatePercentChange({
        initialValue: val_90d,
        finalValue: val,
    });

    return {
        val,
        val_7d,
        val_30d,
        val_90d,
        chg_7d_amt,
        chg_7d_pct,
        chg_30d_amt,
        chg_30d_pct,
        chg_90d_amt,
        chg_90d_pct,
    };
}

/**
 * Calculates RWA asset count by subtracting stablecoin count from total count
 */
export function getRwaAssetCountFromManager(manager: Manager): number {
    const totalCount = manager.asset_count ? Number(manager.asset_count) : 0;

    const stablecoinStats = manager.asset_class_stats?.find(
        (ac) => ac.id === ASSET_CLASS_IDS.Stablecoin,
    );

    if (!stablecoinStats) {
        return totalCount;
    }

    return totalCount - (stablecoinStats.asset_count || 0);
}

// // // //

interface NetworkStats {
    id: number;
    name: string;
    slug: string;
    icon_url: string | null;
    color_hex: string | null;
    asset_count: number;
    description: string | null;
    bridged_token_value_dollar: MeasureValue;
    circulating_asset_value_dollar: MeasureValue;
}

interface AssetClassStats {
    id: number;
    name: string;
    slug: string;
    icon_url: string | null;
    color_hex: string | null;
    asset_count: number;
    description: string | null;
    bridged_token_value_dollar: MeasureValue;
    circulating_asset_value_dollar: MeasureValue;
    holding_addresses_count: MeasureValue;
}

interface PlatformStats {
    id: number;
    name: string;
    slug: string;
    icon_url: string | null;
    color_hex: string | null;
    asset_count: number;
    description: string | null;
    bridged_token_value_dollar: MeasureValue;
    circulating_asset_value_dollar: MeasureValue;
}

interface IssuerStats {
    id: string;
    name: string;
    slug: string;
    icon_url: string | null;
    color_hex: string | null;
    asset_count: number;
    description: string | null;
    bridged_token_value_dollar: MeasureValue;
    circulating_asset_value_dollar: MeasureValue;
}

export interface Manager extends FnlSummariesManager {
    network_stats: NetworkStats[];
    asset_class_stats: AssetClassStats[];
    platform_stats: PlatformStats[];
    issuer_stats: IssuerStats[];
}

// ENHANCEMENT - move distributed + represented value object into the API
export interface ManagerWithAggregates extends Manager {
    rwa_asset_count: number;
    rwa_holders_count: MeasureValue | null;
    distributed_value: {
        val: number;
        val_30d: number;
        chg_30d_pct: number;
    };
    represented_value: {
        val: number;
        val_30d: number;
        chg_30d_pct: number;
    };
}

export enum ManagerColumns {
    id = "id",
    slug = "slug",
    name = "name",
    description = "description",
    colorHex = "color_hex",
    iconUrl = "icon_url",
    assetCount = "asset_count",
    bridgedTokenValueDollar = "bridged_token_value_dollar",
    distributedValue = "distributed_value",
    representedValue = "represented_value",
    holdingAddressesCount = "holding_addresses_count",
    trailing30DayTransferVolume = "trailing_30_day_transfer_volume",
    trailing30DayActiveAddressesCount = "trailing_30_day_active_addresses_count",
}
